import React, { useMemo } from 'react'
import { useHome } from '../../hooks/useHome'
import { useGlobal } from '../../hooks/useGlobal'
import { useNoticias } from '../../hooks/useNoticias'
import { useImageLoader } from '../../hooks/useImageLoader'
import { FirstSectionCard, FirstSectionCardMobile, SecondCard, SecondCardMobile, ThirdCard, FourthCard, NewsCarousel } from '../cards'
import { useIsMobile } from '../../hooks/useIsMobile'
import { LoadingSpinner } from '../utils'
import { buildFileUrl } from '../../config/strapi'
import { EmblaOptionsType } from 'embla-carousel'

export function HomePage() {
  const { data: home, loading: homeLoading, error: homeError, refetch: homeRefetch } = useHome()
  const { loading: globalLoading, error: globalError } = useGlobal()
  const { data: noticiasData, loading: noticiasLoading, error: noticiasError } = useNoticias()
  const firstCard = home?.dynamic_zone[0]
  const secondCard = home?.dynamic_zone[1]
  const thirdCard = home?.dynamic_zone[2]
  const fourthCard = home?.dynamic_zone[3]
  const isMobile = useIsMobile()

  // Opções do Embla Carousel
  const emblaOptions: EmblaOptionsType = {
    align: 'center',
    loop: true,
    skipSnaps: false,
    dragFree: false,
    containScroll: 'trimSnaps',
    slidesToScroll: 1
  }

  // Extrair todas as URLs de imagens dos cards
  const imageUrls = useMemo(() => {
    const urls: string[] = []
    
    // FirstCard - background image
    if (firstCard?.background?.url) {
      urls.push(buildFileUrl(firstCard.background.url))
    }
    
    // SecondCard - image
    if (secondCard?.image?.url) {
      urls.push(buildFileUrl(secondCard.image.url))
    }
    
    // FourthCard - image
    if (fourthCard?.image?.url) {
      urls.push(buildFileUrl(fourthCard.image.url))
    }
    
    // FourthCard - background (fallback)
    if (fourthCard?.background?.url) {
      urls.push(buildFileUrl(fourthCard.background.url))
    }
    
    return urls
  }, [firstCard, secondCard, fourthCard])

  // Aguardar carregamento das imagens
  const { isLoading: imagesLoading, progress } = useImageLoader({ images: imageUrls })

  // Aguardar que tanto os dados quanto as imagens estejam carregados
  const isLoading = homeLoading || globalLoading || imagesLoading || noticiasLoading
  const hasError = homeError || globalError || noticiasError

  if (isLoading) {
    const message = imagesLoading 
      ? `Carregando... ${Math.round(progress)}%`
      : 'Carregando...'
    
    return <LoadingSpinner message={message} progress={imagesLoading ? progress : undefined} />
  }

  if (hasError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>Erro ao carregar dados</h2>
        <p>{homeError || globalError || noticiasError}</p>
        <button
          onClick={homeRefetch}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  // Extrair notícias para o carrossel
  const noticias = noticiasData?.list_noticias || []

  return (
    <div className="home-page">
      {/* Renderiza o firstCard da dynamic_zone */}
      {firstCard && (
        isMobile ? <FirstSectionCardMobile card={firstCard} /> : <FirstSectionCard card={firstCard} />
      )}
      
      {/* Carrossel de notícias */}
      <NewsCarousel news={noticias} options={emblaOptions} />
      
      {/* Renderiza o secondCard da dynamic_zone */}
      {secondCard && (
        isMobile ? <SecondCardMobile card={secondCard} /> : <SecondCard card={secondCard} />
      )}
      {/* Renderiza o thirdCard da dynamic_zone */}
      {thirdCard && (
        <ThirdCard card={thirdCard} />
      )}
      {/* Renderiza o fourthCard da dynamic_zone */}
      {fourthCard && (
        <FourthCard card={fourthCard} />
      )}
    </div>
  )
} 